import { useEffect, useMemo, useState } from "react";
import { authAxios } from "../../utils/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  RefreshCw,
  Play,
  Eye,
  ShieldAlert,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Unlock,
} from "lucide-react";
import { toast } from "sonner";
import StageTrajectories from "./StageTrajectories";
import InferenceAccuracy from "./InferenceAccuracy";
import OpsTiles from "./OpsTiles";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90_000;

const statusBadge = (session) => {
  const pending = session.pending;
  const latest = session.latest_digest;
  if (pending?.status === "running") {
    return {
      label: "Running",
      tone: "running",
      icon: <Clock className="w-3 h-3 mr-1 animate-pulse" />,
    };
  }
  if (pending?.status === "queued") {
    return {
      label: "Queued",
      tone: "queued",
      icon: <Clock className="w-3 h-3 mr-1" />,
    };
  }
  if (pending?.status === "failed") {
    return {
      label: "Failed",
      tone: "failed",
      icon: <AlertTriangle className="w-3 h-3 mr-1" />,
    };
  }
  if (latest?.safety_review_flag) {
    return {
      label: "Safety Review",
      tone: "safety",
      icon: <ShieldAlert className="w-3 h-3 mr-1" />,
    };
  }
  if (latest) {
    return {
      label: "Digested",
      tone: "done",
      icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
    };
  }
  return { label: "None", tone: "none", icon: null };
};

const toneClass = {
  running: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  queued: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
  safety: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  done: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  none: "bg-white/5 text-zinc-400 border-white/10",
};

export default function SessionDigests() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [triggeringIds, setTriggeringIds] = useState(new Set());
  const [resettingIds, setResettingIds] = useState(new Set());
  const [dialogDigest, setDialogDigest] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const handleResetEdits = async (session) => {
    if (!session.match_id || !session.user_id) return;
    const matchLabel = session.match_name || session.match_id;
    if (
      !window.confirm(
        `Reset all user_locked / user_edited flags on claims for ${matchLabel}?\n\n` +
          `This lets the digest LLM re-evaluate this relationship from scratch ` +
          `at the next session. Claim text + values are preserved — only the ` +
          `user-edit flags are cleared.`
      )
    ) {
      return;
    }
    setResettingIds((prev) => new Set(prev).add(session.session_id));
    try {
      const params = new URLSearchParams({ user_id: session.user_id });
      const res = await authAxios.post(
        `/admin/ops/reset-user-edits/${session.match_id}?${params.toString()}`
      );
      toast.success(res.data.message || "User edits reset.");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to reset user edits"
      );
    } finally {
      setResettingIds((prev) => {
        const n = new Set(prev);
        n.delete(session.session_id);
        return n;
      });
    }
  };

  const fetchSessions = async () => {
    try {
      const params = new URLSearchParams({ attached_only: "true", limit: "50" });
      if (userFilter.trim()) params.set("user_id", userFilter.trim());
      const res = await authAxios.get(
        `/admin/session-digests/coaching-sessions/list?${params.toString()}`
      );
      setSessions(res.data.sessions || []);
    } catch (err) {
      console.error("Failed to fetch coaching sessions", err);
      toast.error(err.response?.data?.detail || "Failed to load sessions");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    await fetchSessions();
  };

  const pollUntilDone = async (sessionId) => {
    const started = Date.now();
    while (Date.now() - started < POLL_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      try {
        const res = await authAxios.get(
          `/admin/session-digests/pending/${sessionId}`
        );
        const pending = res.data.pending;
        const latest = res.data.latest_digest;
        if (!pending || pending.status === "done" || pending.status === "failed") {
          return { pending, latest };
        }
      } catch (err) {
        console.error("poll failure", err);
      }
    }
    return null;
  };

  const handleTrigger = async (sessionId) => {
    setTriggeringIds((s) => new Set(s).add(sessionId));
    try {
      await authAxios.post("/admin/session-digests/process", {
        session_id: sessionId,
        force: true,
      });
      toast.success("Digest enqueued — polling for completion…");
      // Refresh to show "queued" / "running"
      fetchSessions();
      const result = await pollUntilDone(sessionId);
      if (!result) {
        toast.warning("Digest still running after 90s; refresh manually");
      } else if (result.pending?.status === "failed") {
        toast.error(
          `Digest failed: ${result.pending?.last_error || "unknown error"}`
        );
      } else if (result.latest?.safety_review_flag) {
        toast.warning("Digest completed but flagged for safety review");
      } else {
        toast.success("Digest complete");
      }
      fetchSessions();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to enqueue digest");
    } finally {
      setTriggeringIds((s) => {
        const next = new Set(s);
        next.delete(sessionId);
        return next;
      });
    }
  };

  const handleView = async (digestId) => {
    setDialogLoading(true);
    setDialogDigest({ digest_id: digestId });
    try {
      const res = await authAxios.get(`/admin/session-digests/${digestId}`);
      setDialogDigest(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load digest output");
      setDialogDigest(null);
    } finally {
      setDialogLoading(false);
    }
  };

  const rows = useMemo(() => sessions, [sessions]);

  return (
    <div className="space-y-6" data-testid="admin-session-digests">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-divine-gold">
            Session Digests
          </h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Manually run the Chat Digest Memory routine against any ErosCoach
            session. Digests produce structured memory updates (relational
            claims, timeline events, stage signal, outbound copy outcomes) via
            Gemini 3 Flash by default.
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder="Filter by user_id"
            className="w-56 bg-divine-charcoal border-divine-gold/20"
            data-testid="digest-user-filter"
            onKeyDown={(e) => e.key === "Enter" && fetchSessions()}
          />
          <Button
            variant="outline"
            onClick={refresh}
            disabled={refreshing}
            data-testid="digest-refresh-btn"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      <OpsTiles sessions={rows} />

      <div className="rounded-lg border border-divine-gold/20 bg-divine-charcoal overflow-hidden">
        {/* Desktop / tablet header — hidden on mobile in favour of the
            two-line stacked rows below. */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-wider text-zinc-400 border-b border-white/5">
          <div className="col-span-4">Session</div>
          <div className="col-span-2">Match</div>
          <div className="col-span-1 text-center">Msgs</div>
          <div className="col-span-1 text-center">Dedupe</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="px-4 py-8 text-center text-zinc-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-zinc-400">
            No coaching sessions with a match yet.
          </div>
        ) : (
          rows.map((session) => {
            const badge = statusBadge(session);
            const isTriggering = triggeringIds.has(session.session_id);
            const latestDigestId = session.latest_digest?.digest_id;
            const dedupeVal =
              session.latest_digest?.routing_results?.dedupe_summary
                ?.total_dedupes;
            const dedupeTone =
              dedupeVal == null
                ? "text-zinc-600"
                : dedupeVal > 10
                  ? "text-rose-400"
                  : dedupeVal > 3
                    ? "text-amber-300"
                    : "text-emerald-300";
            return (
              <div
                key={session.session_id}
                className="flex flex-col gap-2 px-4 py-3 border-b border-white/5 hover:bg-white/2 last:border-b-0 md:grid md:grid-cols-12 md:items-center md:gap-2"
                data-testid={`digest-row-${session.session_id}`}
              >
                {/* MOBILE row 1: session + match (each on its own sub-line).
                    DESKTOP: split across col-span-4 + col-span-2 cells. */}
                <div className="flex flex-col gap-1 md:contents">
                  <div className="md:col-span-4 min-w-0">
                    <div className="text-sm text-divine-cream truncate">
                      {session.title || "Untitled coaching session"}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono truncate">
                      {session.session_id}
                    </div>
                  </div>
                  <div className="md:col-span-2 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 md:hidden mt-1">
                      Match
                    </div>
                    <div className="text-sm text-divine-cream truncate">
                      {session.match_name || "—"}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono truncate">
                      {session.match_id || "(unattached)"}
                    </div>
                  </div>
                </div>

                {/* MOBILE row 2: msgs + dedupe + status + actions on one
                    horizontal row. DESKTOP: each lives in its own grid cell. */}
                <div className="flex items-center justify-between gap-3 flex-wrap md:contents">
                  <div className="flex items-center gap-3 text-xs md:hidden">
                    <span className="text-zinc-400">
                      {session.message_count} msg
                      {session.message_count === 1 ? "" : "s"}
                    </span>
                    <span
                      className={`tabular-nums ${dedupeTone}`}
                      title={
                        dedupeVal == null
                          ? "No digest yet"
                          : `${dedupeVal} write(s) skipped by dedupe`
                      }
                      data-testid={`digest-dedupe-${session.session_id}`}
                    >
                      dedupe: {dedupeVal ?? "—"}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] ${toneClass[badge.tone]}`}
                    >
                      {badge.icon}
                      {badge.label}
                    </span>
                  </div>
                  {/* Desktop-only cells */}
                  <div className="hidden md:block md:col-span-1 text-center text-sm text-zinc-300">
                    {session.message_count}
                  </div>
                  <div
                    className="hidden md:block md:col-span-1 text-center text-sm"
                    data-testid={`digest-dedupe-desktop-${session.session_id}`}
                  >
                    {dedupeVal == null ? (
                      <span className="text-zinc-600">—</span>
                    ) : (
                      <span
                        className={`tabular-nums ${dedupeTone}`}
                        title={`${dedupeVal} write(s) skipped by dedupe in the latest digest`}
                      >
                        {dedupeVal}
                      </span>
                    )}
                  </div>
                  <div className="hidden md:block md:col-span-2">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full border text-xs ${toneClass[badge.tone]}`}
                    >
                      {badge.icon}
                      {badge.label}
                    </span>
                  </div>
                  <div className="flex gap-1 justify-end md:col-span-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleResetEdits(session)}
                      disabled={
                        resettingIds.has(session.session_id) ||
                        !session.match_id ||
                        !session.user_id
                      }
                      title="Clear user_locked / user_edited flags on all claims for this (user, match)"
                      data-testid={`digest-reset-edits-btn-${session.session_id}`}
                    >
                      <Unlock className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleTrigger(session.session_id)}
                      disabled={
                        isTriggering ||
                        session.pending?.status === "running" ||
                        session.pending?.status === "queued"
                      }
                      data-testid={`digest-trigger-btn-${session.session_id}`}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      {isTriggering ? "Running…" : "Trigger"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleView(latestDigestId)}
                      disabled={!latestDigestId}
                      data-testid={`digest-view-btn-${session.session_id}`}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={!!dialogDigest} onOpenChange={(o) => !o && setDialogDigest(null)}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] bg-divine-charcoal border-divine-gold/30"
          data-testid="digest-output-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-divine-gold">
              Digest Output
              {dialogDigest?.digest_id && (
                <span className="ml-2 text-xs font-mono text-zinc-500">
                  {dialogDigest.digest_id}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            {dialogLoading ? (
              <div className="py-8 text-center text-zinc-400">Loading…</div>
            ) : dialogDigest ? (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase text-zinc-500 mb-1">
                      AI Model
                    </div>
                    <div className="font-mono text-divine-cream">
                      {dialogDigest.ai_model_used || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-zinc-500 mb-1">
                      Duration
                    </div>
                    <div className="font-mono text-divine-cream">
                      {dialogDigest.ai_call_duration_ms ?? "—"} ms
                    </div>
                  </div>
                </div>
                {dialogDigest.safety_review_flag && (
                  <div className="rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-rose-300 text-xs flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <strong>Safety review flagged</strong> — memory writes
                      were deferred.
                      <div className="mt-1 text-rose-200/80">
                        {dialogDigest.safety_review_reason || "No reason recorded."}
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase text-zinc-500 mb-1">
                    Routing Results
                  </div>
                  <pre className="bg-black/40 border border-white/10 rounded p-3 text-xs overflow-x-auto text-zinc-300">
                    {JSON.stringify(dialogDigest.routing_results || {}, null, 2)}
                  </pre>
                </div>
                <div>
                  <div className="text-xs uppercase text-zinc-500 mb-1">
                    Raw Digest Output
                  </div>
                  <pre className="bg-black/40 border border-white/10 rounded p-3 text-xs overflow-x-auto text-zinc-300">
                    {JSON.stringify(dialogDigest.digest_output || {}, null, 2)}
                  </pre>
                </div>
              </div>
            ) : null}
          </ScrollArea>
        </DialogContent>
      </Dialog>
      {/* Memory Plan v1 — Phase 4 stage tracker section */}
      <StageTrajectories />
      {/* Memory Plan v1 — Phase 5a calibration metrics */}
      <InferenceAccuracy />
    </div>
  );
}
